/**
 * 렌더된 청첩장 HTML의 섹션 순서를 사용자 설정(settings.sectionOrder)대로 재배열한다.
 *
 * 청첩장 실제 표시 순서는 각 테마 Liquid 템플릿의 `<section data-section="X">` 물리 배치로
 * 고정돼 있다(템플릿에 `{% for %}` 루프 없음). 이 헬퍼는 Liquid 렌더가 끝난 완성 HTML
 * 문자열에서 재정렬 대상 섹션 블록만 골라 사용자 순서대로 다시 배치한다.
 *
 * - 재정렬 대상: `REORDERABLE_SECTIONS`에 속하는 `<section>`만(directions 포함).
 * - ending/bgm 등 재정렬 대상이 아닌 `<section>`은 물리적 위치에 고정
 *   (그 사이를 재정렬 섹션이 흐른다 — "슬롯 방식").
 * - cover/footer는 `<div data-section>`이라 `<section>` 스코프 매처가 애초에 건드리지 않음.
 *
 * ⚠️ sectionOrder가 미설정(undefined)이면 반드시 원본을 그대로 반환한다.
 *    DEFAULT_SECTION_ORDER는 어느 템플릿의 물리 순서와도 일치하지 않으므로,
 *    default로 채워 재배열하면 기존 청첩장 레이아웃이 전부 바뀌는 사고가 난다.
 */
import { REORDERABLE_SECTIONS, sanitizeSectionOrder, } from '../schemas/invitation.js';
/**
 * `<section ... data-section="X"> ... </section>` 블록 매처.
 * - `[^>]*`가 속성 순서 변동을 흡수하고 여는 `>`를 넘지 못한다.
 * - 각 테마 템플릿의 `<section>`은 중첩 없이 형제로 나열되므로 non-greedy `[\s\S]*?</section>`가 안전하다.
 */
const SECTION_BLOCK_RE = /<section\b[^>]*\bdata-section="([^"]+)"[\s\S]*?<\/section>/g;
const REORDERABLE_SET = new Set(REORDERABLE_SECTIONS);
function collectSectionMatches(html) {
    const matches = [];
    // 전역 정규식 재사용 시 lastIndex 오염을 피하기 위해 매 호출마다 새 인스턴스 사용
    const re = new RegExp(SECTION_BLOCK_RE.source, 'g');
    let m;
    while ((m = re.exec(html)) !== null) {
        matches.push({ id: m[1], text: m[0], start: m.index, end: m.index + m[0].length });
    }
    return matches;
}
/**
 * 완성 HTML에서 재정렬 대상 섹션의 물리적 순서(문서 등장 순서)를 추출한다.
 * 에디터 순서 편집 UI를 실제 템플릿 레이아웃에 맞춰 시딩할 때 사용.
 */
export function extractReorderableOrder(html) {
    return collectSectionMatches(html)
        .map((s) => s.id)
        .filter((id) => REORDERABLE_SET.has(id));
}
/**
 * 렌더된 HTML의 섹션을 sectionOrder대로 재배열한다.
 * sectionOrder가 없거나 재배열이 불필요하면 원본을 그대로 반환한다.
 */
export function reorderSectionsInHtml(html, sectionOrder) {
    // 1) 하위호환 가드 — 미설정이면 손대지 않는다.
    if (!sectionOrder || sectionOrder.length === 0)
        return html;
    // 2) 모든 <section> 수집
    const matches = collectSectionMatches(html);
    // 3) 재정렬 슬롯(문서 순서의 reorderable 섹션들)
    const present = matches.filter((s) => REORDERABLE_SET.has(s.id));
    // 4) 슬롯이 2개 미만이면 재배열 의미 없음
    if (present.length < 2)
        return html;
    // 5) 사용자 순서를 실제 존재하는 섹션으로 제한
    const presentIds = new Set(present.map((s) => s.id));
    const desired = sanitizeSectionOrder(sectionOrder).filter((id) => presentIds.has(id));
    // 6) 안전 bail — 개수 불일치(중복 data-section 등)면 원본 유지
    if (desired.length !== present.length)
        return html;
    // 7) no-op skip — 이미 원하는 순서면 문자열 작업 회피
    let identical = true;
    for (let i = 0; i < present.length; i++) {
        if (present[i].id !== desired[i]) {
            identical = false;
            break;
        }
    }
    if (identical)
        return html;
    // 8) 재구성 — 재정렬 슬롯에는 desired 순서의 블록을, 고정 섹션은 원본 그대로.
    const blockById = new Map();
    for (const s of present)
        blockById.set(s.id, s.text);
    const desiredQueue = [...desired];
    const parts = [];
    let cursor = 0;
    for (const match of matches) {
        // 섹션 사이 gap(공백·주석·cover/footer div 등) 보존
        parts.push(html.slice(cursor, match.start));
        if (REORDERABLE_SET.has(match.id)) {
            // 재정렬 슬롯 — desired 큐에서 다음 블록을 꺼내 채운다
            const nextId = desiredQueue.shift();
            parts.push(blockById.get(nextId));
        }
        else {
            // 고정 섹션 — 원본 위치·내용 유지
            parts.push(match.text);
        }
        cursor = match.end;
    }
    // 마지막 섹션 이후 나머지(</body> 등) 보존
    parts.push(html.slice(cursor));
    return parts.join('');
}
//# sourceMappingURL=reorderSections.js.map